import { Inject, Injectable } from '@angular/core';

import { APIService } from '@fry/lib/api';
import { ApiStore, DBDoc, TableSource } from '@fry/lib/store';

import { ASYNC_CONFIG, AsyncConfig } from '../app/async_config';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AsyncTaskAvailableActions, AsyncTaskOptionStep, AsyncTaskOptionStepForm, AsyncTaskStates, AsyncTaskWizardDoc, AsyncTaskWizardSteps  } from './async-task.interface';
import { AsyncTaskWizard } from './async-task-wizard';
import { Observable, throwError } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class AsyncTasksWizardStore extends ApiStore<AsyncTaskWizard, AsyncTaskWizardDoc> {
    AssociatedModel = AsyncTaskWizard

    docType = 'celeryTask';
    endpoint = 'async-tasks';

    constructor(
      api: APIService,
      @Inject(ASYNC_CONFIG) private async_config: AsyncConfig,
    ) {
      super(api);
    }

    public blank(defaults?: Partial<DBDoc>): AsyncTaskWizard {
      defaults = defaults || {};
      const doc: AsyncTaskWizardDoc = {
        _id: '__new__',
        tool: this.async_config.id,
        itemDocType: defaults.itemDocType,
        state: AsyncTaskStates.DRAFT,
        wizardData: { currentStep: AsyncTaskWizardSteps.STEP_2, selectedAction: undefined },
        data: {
          source: {
            ids: [],
          },
          params: {}
        },
        ...(defaults || {})
      };
      return this.createObject(doc);
    }

    public update(oid: string, doc: any): Observable<any> {
      return this.api.put(`${this.endpoint}/${oid}/edit`, doc)
        .pipe(
          switchMap(res => this.getForEdit(res.id)),
          catchError(error => {
            console.log('Received an error:', error);
            return throwError(() => new Error(error));
          }),
          tap(() => {
            this.invalidate();
          })
        );
    }

    public summary(oid: string): Observable<any> {
      return this.api.get(`${this.endpoint}/${oid}/output`)
        .pipe(
          catchError(error => {
            console.log('Received an error:', error);
            return throwError(() => new Error(error));
          }),
          tap(() => {
            this.invalidate();
          })
        );
    }

    getAvailableActions(oid: string) : Observable<AsyncTaskAvailableActions> {
        return this.api.get(`${this.endpoint}/${oid}/wizard/available_actions`)
    }

    getActionSteps(oid: string) : Observable<AsyncTaskOptionStep[]> {
      return this.api.get(`${this.endpoint}/${oid}/wizard/get_action_steps`)
    }

    getActionForm(oid: string, data: { step: string; data: any }) : Observable<AsyncTaskOptionStepForm> {
        return this.api.post(`${this.endpoint}/${oid}/wizard/get_action_form`, data)
    }

    getActionDetails(oid: string) : Observable<AsyncTaskOptionStepForm> {
      return this.api.get(`${this.endpoint}/${oid}/wizard/get_action_details`);
    }

    start(taskAction: string, data: any) {
      return this.api.post(`${this.endpoint}/start/${taskAction}`, data);
    }

    trigger(taskId: string) {
      return this.api.post(`${this.endpoint}/${taskId}/trigger`, {});
    }

    rerun(taskId: string, options: { failedOnly?: boolean }) {
      return this.api.post(`${this.endpoint}/${taskId}/rerun`, options);
    }

    abort(taskId: string) {
      return this.api.put(`${this.endpoint}/abort/${taskId}`, {});
    }

    download(id: string) {
      return this.api.get(`${this.endpoint}/${id}/download`).pipe(
        map(result => {
          window.location.href = result.url;
        })
      );
    }

    selectTableSource(id: string): TableSource {
      return {
        table: (params: any) => {
          return this.api.post(`${this.endpoint}/${id}/select/table`, params)
          .pipe(
            catchError(error => {
              console.log('Received an error:', error.message);
              return throwError(() => error);
            })
          );
        },
        startExport: (listId, params) => {
          const payload = {
            params,
            options: params.tableOptions,
            listId
          };
          return this.api.post(`${this.endpoint}/start-export`, payload);
        }
      }
    }

    itemsTableSource(id: string): TableSource {
      return {
        table: (params: any) => {
          return this.api.post(`${this.endpoint}/${id}/items/table`, params)
          .pipe(
            catchError(error => {
              console.log('Received an error:', error.message);
              return throwError(() => error);
            })
          );
        },
        startExport: (listId, params) => {
          const payload = {
            params,
            options: params.tableOptions,
            listId
          };
          return this.api.post(`${this.endpoint}/start-export`, payload);
        }
      }
    }

    confirmTableSource(id: string): TableSource {
      return {
        table: (params: any) => {
          return this.api.post(`${this.endpoint}/${id}/confirm/table`, params)
          .pipe(
            catchError(error => {
              console.log('Received an error:', error.message);
              return throwError(() => error);
            })
          );
        },
        startExport: (listId, params) => {
          const payload = {
            params,
            options: params.tableOptions,
            listId
          };
          return this.api.post(`${this.endpoint}/start-export`, payload);
        }
      }
    }

    outputTableSource(id: string): TableSource {
      return {
        table: (params: any) => {
          return this.api.post(`${this.endpoint}/${id}/output/table`, params)
          .pipe(
            catchError(error => {
              console.log('Received an error:', error.message);
              return throwError(() => error);
            })
          );
        },
        startExport: (listId, params) => {
          const payload = {
            params,
            listId,
          }
          return this.api.post(`${this.endpoint}/${id}/output/start-export`, payload);
        }
      }
    }

}
